import { useCallback, useContext, useState } from "react"
import { SetValueConfig } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useUser } from "@/apis/bff"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { usePatientLayoutContext } from "@/contexts/patient-layout-context"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { CustomSofiaEvent, sofiaEventDispatch } from "@/hooks/useSofiaEventListener"
import { joinStrings } from "@/lib/joinString"
import { getPersistedLanguage } from "@/lib/language-storage"
import { selectContentForLanguage } from "@/lib/select-content-for-language"
import { PreventionProgramsDialog } from "@/modules/patients/detail/health-information/condition/prevention-programs-dialog"
import { PreventionProgramDetails } from "@/modules/patients/detail/progress-note/hooks/useAppointmentPreventionPrograms"
import { useFindClosestPreventionCareProvider } from "@/modules/patients/detail/progress-note/hooks/useFindClosestPreventionCareProvider"
import { ProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useReferralStoreContext } from "@/modules/patients/detail/referral/store/useReferralStore"
import { usePreventionProgramsStoreContext } from "@/modules/patients/detail/store/usePreventionProgramsStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useTreatmentPlanForPreventionProgramsQuery } from "@/modules/patients/services/useTreatmentPlanForPreventionProgramsQuery"

interface PreventionProgramInlineBannerProps {
  preventionProgram: PreventionProgramDetails
  patientId: string
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
}

const formOptions: SetValueConfig = { shouldDirty: true, shouldValidate: true }

export const PreventionProgramInlineBanner = (props: PreventionProgramInlineBannerProps) => {
  const { preventionProgram, patientId, episodeOfCareProgressNoteData } = props
  const { t } = useTranslation()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isFetching, setIsFetching] = useState(false)

  const { objectId: agentObjectId } = useUser()
  const country = useCountryParam()
  const language = getPersistedLanguage()
  const actionedPrograms = usePreventionProgramsStoreContext((state) => state.actionedPreventionPrograms)
  const markAsReferred = usePreventionProgramsStoreContext((state) => state.markPreventionProgramAsReferred)
  const markAsDismissed = usePreventionProgramsStoreContext((state) => state.markPreventionProgramAsDismissed)
  const setCareProvider = useReferralStoreContext((state) => state.setCareProvider)
  const progressNoteFormStore = useContext(ProgressNoteFormContext)
  if (!progressNoteFormStore) throw new Error("Missing ProgressNoteFormProvider in the tree")

  const { countryPatient } = usePatientLayoutContext()

  const { findClosestCareProvider } = useFindClosestPreventionCareProvider()

  const fetchTreatmentPlanForPreventionPrograms = useTreatmentPlanForPreventionProgramsQuery()

  const handleDismiss = () => setIsDialogOpen(true)

  const handleRefer = useCallback(async () => {
    const { id: progressNoteId, appointment } = episodeOfCareProgressNoteData

    const progressNoteForm = progressNoteFormStore.getState().progressNoteForms.get(progressNoteId)
    const pointOfCareForm = progressNoteForm?.pointOfCareForm
    const treatmentPlanForm = progressNoteForm?.treatmentPlanForm

    // Auto-select closest care provider for Radiology center aka "ZIP case" via mutation
    if (pointOfCareForm) {
      pointOfCareForm.setValue("medicalPointOfCare", preventionProgram.pointOfCare, formOptions)
      pointOfCareForm.setValue("actualPointOfCare", null, formOptions)

      // Auto-select closest care provider for ZIP case via mutation
      if (preventionProgram.matchedCase === "ZIP" && countryPatient.address) {
        const { countryCode, city, houseNumberStreet, postalCode } = countryPatient.address

        const closestCareProviderId = await findClosestCareProvider({
          patientId,
          address: { countryCode, city, houseNumberStreet, postalCode },
          preventionProgramKey: preventionProgram.key,
        })

        if (closestCareProviderId) {
          pointOfCareForm.setValue("careProviderId", closestCareProviderId, formOptions)
          setCareProvider(closestCareProviderId)
        }
      }
    }

    // Append treatment plan template if available — fetched lazily here (rather than as part of
    // the appointment fragment) to avoid the HC13 Fusion query-plan duplication that the deep
    // selection through relatedPreventionPrograms.analysis.treatmentPlanTemplate triggers.
    let treatmentPlanTemplateApplied = false
    if (treatmentPlanForm && preventionProgram.treatmentPlanTemplateId) {
      setIsFetching(true)
      try {
        const response = await fetchTreatmentPlanForPreventionPrograms(preventionProgram.treatmentPlanTemplateId)
        const treatmentPlanTemplate = response?.treatmentPlanTemplateById
        const { language } = appointment

        // Find the template language matching the appointment language
        const templateLanguage = treatmentPlanTemplate?.languages.find((lang) => lang.language === language)

        if (templateLanguage && !treatmentPlanTemplate?.isDraft) {
          treatmentPlanForm.setValue("title", templateLanguage.title, formOptions)
          treatmentPlanForm.setValue("recommended", templateLanguage.recommendedTreatment, formOptions)
          treatmentPlanForm.setValue("followUpInstructions", templateLanguage.followUpInstructions, formOptions)
          treatmentPlanTemplateApplied = true
        }
      } finally {
        setIsFetching(false)
      }
    }

    // Only dispatch event if a treatment plan template was actually applied
    if (treatmentPlanTemplateApplied) {
      sofiaEventDispatch(CustomSofiaEvent("TREATMENT_PLAN_SUGGESTION_APPEND_EVENT", { progressNoteId }))
    }

    const displayedInstruction = preventionProgram.localizedInstruction.en

    FaroInsightsWrapper.pushEvent("prevention_referral_from_progress_note", {
      patientId,
      agentObjectId,
      preventionProgramName: preventionProgram.name.en,
      displayedUserInstruction: displayedInstruction,
    })

    markAsReferred(preventionProgram.id)
  }, [
    agentObjectId,
    countryPatient.address,
    episodeOfCareProgressNoteData,
    findClosestCareProvider,
    language,
    markAsReferred,
    patientId,
    preventionProgram,
    progressNoteFormStore,
    fetchTreatmentPlanForPreventionPrograms,
    setCareProvider,
  ])

  if (actionedPrograms[preventionProgram.id]) return null

  const instruction = selectContentForLanguage(language, preventionProgram.localizedInstruction)

  const programName = selectContentForLanguage(language, preventionProgram.name)

  return (
    <>
      <Banner type="inline" variant="info" iconClassName="self-auto">
        <div className="flex w-full items-center justify-between gap-2">
          <span className="min-w-0 text-sm">{joinStrings([programName, instruction], ": ")}</span>
          <div className="flex justify-end">
            <Button
              variant="tertiary"
              onClick={handleDismiss}
              disabled={isFetching}
              className="text-on-surface whitespace-nowrap">
              {t("episodeOfCare.progressNote.preventionProgram.dismiss")}
            </Button>
            <Button
              variant="tertiary"
              onClick={handleRefer}
              isLoading={isFetching}
              disabled={isFetching}
              className="text-on-surface whitespace-nowrap">
              {t("episodeOfCare.progressNote.preventionProgram.refer")}
            </Button>
          </div>
        </div>
      </Banner>

      {isDialogOpen && (
        <PreventionProgramsDialog
          isEditPreventionProgramsMode
          preventionProgramData={preventionProgram}
          patientId={patientId}
          country={country}
          onClose={() => setIsDialogOpen(false)}
          onDismiss={() => markAsDismissed(preventionProgram.id)}
        />
      )}
    </>
  )
}
